import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  IconButton
} from '@mui/material';
import { Close as CloseIcon, DirectionsCar } from '@mui/icons-material';
import { CarImport } from '../api/apiClient';

interface CarDetailsModalProps {
  open: boolean;
  onClose: () => void;
  car: CarImport | null;
}

const CarDetailsModal: React.FC<CarDetailsModalProps> = ({ open, onClose, car }) => {
  if (!car) return null;

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return 'N/A';
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fi-FI');
  };

  const DetailRow = ({ label, value }: { label: string; value: string | number | undefined }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
      <Typography variant="body2" color="text.secondary" fontWeight="medium">
        {label}:
      </Typography>
      <Typography variant="body2">
        {value || 'N/A'}
      </Typography>
    </Box>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
        color: 'white',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DirectionsCar />
          <Typography variant="h6" component="div">
            {car.make} {car.model} {car.year ? `(${car.year})` : ''}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {/* Basic Information */}
            <Box sx={{ flex: 1, minWidth: '300px' }}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                🚗 Basic Information
              </Typography>
              <DetailRow label="Make" value={car.make} />
              <DetailRow label="Model" value={car.model} />
              <DetailRow label="Year" value={car.year} />
              <DetailRow label="Engine Size" value={car.engine_size ? `${car.engine_size}L` : undefined} />
              <DetailRow label="Fuel Type" value={car.fuel_type} />
              <DetailRow label="Drive Type" value={car.drive_type} />
              <DetailRow label="Body Type" value={car.body_type} />
              <DetailRow label="Doors" value={car.doors} />
              <DetailRow label="Transmission" value={car.transmission} />
            </Box>

            {/* Financial Information */}
            <Box sx={{ flex: 1, minWidth: '300px' }}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                💰 Tax Information
              </Typography>
              <DetailRow label="Declared Value" value={formatCurrency(car.declared_value)} />
              <DetailRow label="Calculated Tax" value={formatCurrency(car.calculated_tax)} />
              <DetailRow label="Applied Tax Percentage" value={car.applied_tax_percentage ? `${car.applied_tax_percentage}%` : 'N/A'} />
              <DetailRow label="Decision Date" value={formatDate(car.decision_date)} />
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                📋 Additional Details
              </Typography>
              <DetailRow label="Mileage" value={car.mileage_km ? `${car.mileage_km.toLocaleString()} km` : undefined} />
              <DetailRow label="Registration Number" value={car.registration_number} />
              <DetailRow label="VIN" value={car.vin} />
            </Box>
          </Box>

          {/* Individual Characteristics */}
          {car.individual_characteristics && (
            <Box>
              <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                ⚙️ Individual Characteristics
              </Typography>
              <Box sx={{ 
                p: 2, 
                backgroundColor: 'grey.50', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {car.individual_characteristics}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Tax Summary */}
          <Box sx={{ 
            p: 2, 
            backgroundColor: 'primary.50', 
            borderRadius: 2,
            border: '2px solid',
            borderColor: 'primary.200'
          }}>
            <Typography variant="h6" color="primary" gutterBottom>
              💡 Tax Summary
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip 
                label={`Tax: ${formatCurrency(car.calculated_tax)}`}
                color="primary"
                variant="filled"
                sx={{ fontSize: '0.9rem' }}
              />
              <Chip 
                label={`Value: ${formatCurrency(car.declared_value)}`}
                color="secondary"
                variant="outlined"
                sx={{ fontSize: '0.9rem' }}
              />
              <Chip 
                label={`${car.year || 'Unknown'} • ${car.fuel_type || 'Unknown fuel'}`}
                color="default"
                variant="outlined"
                sx={{ fontSize: '0.9rem' }}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CarDetailsModal;
